import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Users, AlertTriangle, Calendar } from 'lucide-react';
import api from '../services/api';

const ClientSelector = ({ 
  selectedClient, 
  onClientChange, 
  showStats = true, 
  placeholder = "Selecionar cliente...",
  className = "" 
}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/complaints/master/clients');
      
      if (response.data.success) {
        setClients(response.data.clients);
      } else {
        throw new Error('Falha ao carregar clientes');
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setError('Erro ao carregar lista de clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    onClientChange(client);
    setIsOpen(false);
  };

  const selectedClientData = clients.find(c => c.client_slug === selectedClient);

  const formatNumber = (num) => {
    return parseInt(num) || 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'text-yellow-600 bg-yellow-100';
      case 'investigating': return 'text-blue-600 bg-blue-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
          <div className="flex items-center">
            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-gray-500">Carregando clientes...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-3 py-2 border border-red-300 rounded-md bg-red-50">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-red-600">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Selector Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
            <span className={selectedClientData ? "text-gray-900" : "text-gray-500"}>
              {selectedClientData ? selectedClientData.client_name : placeholder}
            </span>
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
        
        {/* Stats do cliente selecionado */}
        {showStats && selectedClientData && (
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center">
              <Users className="h-3 w-3 mr-1" />
              {formatNumber(selectedClientData.total_complaints)} total
            </div>
            <div className="flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {formatNumber(selectedClientData.pending_complaints)} pendentes
            </div>
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {formatNumber(selectedClientData.last_30_days)} últimos 30 dias
            </div>
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Opção "Todos os clientes" */}
          <button
            type="button"
            onClick={() => handleClientSelect({ client_slug: 'all', client_name: 'Todos os clientes' })}
            className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center ${
              selectedClient === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
            }`}
          >
            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
            <span className="font-medium">Todos os clientes</span>
          </button>
          
          {/* Lista de clientes */}
          {clients.map((client) => (
            <button
              key={client.client_slug}
              type="button"
              onClick={() => handleClientSelect(client)}
              className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                selectedClient === client.client_slug ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                <span className="font-medium">{client.client_name}</span>
              </div>
              
              {/* Stats do cliente */}
              {showStats && (
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>{formatNumber(client.total_complaints)}</span>
                  {formatNumber(client.pending_complaints) > 0 && (
                    <span className="px-1 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                      {formatNumber(client.pending_complaints)}
                    </span>
                  )}
                </div>
              )}
            </button>
          ))}
          
          {clients.length === 0 && (
            <div className="px-3 py-2 text-gray-500 text-sm">
              Nenhum cliente encontrado
            </div>
          )}
        </div>
      )}

      {/* Overlay para fechar dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ClientSelector;
